import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, getUser } from '../lib/api';
import { dateTime } from '../lib/format';
import { Card, Empty, ErrorNote, Field, Loading, PageHead, Pill, useResource, useToast } from '../components/ui';
import '../styles/children.css';
import { DeveloperTools } from './DeveloperTools';

/**
 * The integration surface, read from the deployment's own OpenAPI document:
 * where to call, with which credential, and how webhooks are signed.
 */

type SecurityRequirement = Record<string, string[]>;

interface Operation {
  tags?: string[];
  summary?: string;
  security?: SecurityRequirement[];
}

interface SecurityScheme { type: string; in?: string; name?: string; description?: string }

interface OpenApiDoc {
  info?: { title?: string; version?: string };
  tags?: Array<{ name: string; description?: string }>;
  paths?: Record<string, Record<string, Operation>>;
  webhooks?: Record<string, Record<string, { summary?: string; description?: string }>>;
  components?: { securitySchemes?: Record<string, SecurityScheme> };
}

const METHODS = ['get', 'post', 'put', 'patch', 'delete'] as const;

const SCHEME_LABEL: Record<string, string> = {
  serviceKey: 'Service key',
  collectionPointKey: 'Collection point key',
};
const schemeLabel = (key: string) =>
  SCHEME_LABEL[key] ?? key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

/** "Collection point key or service key (consent:capture)". */
function credential(security: SecurityRequirement[] | undefined): string {
  if (!security || security.length === 0) return 'None';
  return security
    .map((req) => Object.entries(req)
      .map(([name, scopes]) => `${schemeLabel(name)}${scopes.length ? ` (${scopes.join(', ')})` : ''}`)
      .join(' and '))
    .map((s, i) => (i === 0 ? s : s.charAt(0).toLowerCase() + s.slice(1)))
    .join(' or ');
}

interface Row { method: string; path: string; summary: string; credential: string }

function groupByTag(doc: OpenApiDoc): Array<{ tag: string; rows: Row[] }> {
  const order = (doc.tags ?? []).map((t) => t.name);
  const groups = new Map<string, Row[]>();
  for (const [path, ops] of Object.entries(doc.paths ?? {})) {
    for (const m of METHODS) {
      const op = ops[m];
      if (!op) continue;
      const tag = op.tags?.[0] ?? 'Other';
      if (!groups.has(tag)) groups.set(tag, []);
      groups.get(tag)!.push({ method: m.toUpperCase(), path, summary: op.summary ?? '', credential: credential(op.security) });
    }
  }
  const rank = (t: string) => (order.includes(t) ? order.indexOf(t) : order.length);
  return [...groups.entries()].sort((a, b) => rank(a[0]) - rank(b[0])).map(([tag, rows]) => ({ tag, rows }));
}

/* ------------------------------------------------------------- consent feed */

interface FeedConfig {
  enabled: boolean;
  destination: 'directory' | 's3';
  s3Endpoint: string | null;
  s3Region: string;
  s3Bucket: string | null;
  s3Prefix: string;
  s3KeyRef: string | null;
  s3KeyPresent: boolean;
  scheduleTime: string;
  scheduleTz: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface FeedRun {
  id: string;
  trigger: 'schedule' | 'manual';
  status: 'running' | 'ready' | 'error';
  destination: string;
  startedAt: string;
  finishedAt: string | null;
  rows: number | null;
  uploaded: boolean;
  manifest: { sha256: Record<string, string> } | null;
  error: string | null;
}

interface FeedView { config: FeedConfig; lastRun: FeedRun | null; runs: FeedRun[] }

type FeedForm = Omit<FeedConfig, 's3KeyPresent' | 'updatedBy' | 'updatedAt' | 's3Endpoint' | 's3Bucket' | 's3KeyRef'> & {
  s3Endpoint: string; s3Bucket: string; s3KeyRef: string;
};

const DESTINATION_LABEL: Record<FeedConfig['destination'], string> = {
  directory: 'Exports volume',
  s3: 'Exports volume and S3 bucket',
};

/**
 * Current consent state per principal hash and purpose, written nightly to
 * the exports volume and, when configured, to the customer's bucket. Owner
 * only: the route refuses every other role, and the card is not shown to them.
 */
function ConsentFeedCard() {
  const toast = useToast();
  const [tick, setTick] = useState(0);
  const res = useResource<FeedView>(() => api.get('/v1/settings/consent-feed'), [tick]);
  const [form, setForm] = useState<FeedForm | null>(null);
  const [busy, setBusy] = useState<'save' | 'run' | null>(null);
  const v = res.data;

  function edit() {
    if (!v) return;
    const c = v.config;
    setForm({
      enabled: c.enabled, destination: c.destination, s3Endpoint: c.s3Endpoint ?? '', s3Region: c.s3Region,
      s3Bucket: c.s3Bucket ?? '', s3Prefix: c.s3Prefix, s3KeyRef: c.s3KeyRef ?? '',
      scheduleTime: c.scheduleTime, scheduleTz: c.scheduleTz,
    });
  }

  async function save() {
    if (!form) return;
    setBusy('save');
    try {
      await api.put('/v1/settings/consent-feed', {
        enabled: form.enabled, destination: form.destination,
        s3Endpoint: form.s3Endpoint.trim() || null, s3Region: form.s3Region.trim() || 'ap-south-1',
        s3Bucket: form.s3Bucket.trim() || null, s3Prefix: form.s3Prefix.trim(), s3KeyRef: form.s3KeyRef.trim() || null,
        scheduleTime: form.scheduleTime, scheduleTz: form.scheduleTz.trim() || 'Asia/Kolkata',
      });
      toast('Consent feed saved.', 'ok');
      setForm(null);
      setTick((t) => t + 1);
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
    }
  }

  async function runNow() {
    setBusy('run');
    try {
      const out = await api.post<{ run: FeedRun }>('/v1/settings/consent-feed/run');
      toast(`Feed written: ${out.run.rows ?? 0} rows.`, 'ok');
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(null);
      setTick((t) => t + 1);
    }
  }

  function download(run: FeedRun, file: 'ndjson' | 'csv' | 'manifest') {
    const ext = file === 'manifest' ? 'manifest.json' : `consent-feed.${file}`;
    api.download(`/v1/settings/consent-feed/runs/${run.id}/download?file=${file}`, `${run.id}-${ext}`)
      .catch((err: Error) => toast(err.message, 'bad'));
  }

  const c = v?.config;
  const last = v?.lastRun ?? null;
  const latest = v?.runs[0] ?? null;

  return (
    <Card title="Consent feed">
      {res.loading && !v && <Loading />}
      <ErrorNote error={res.error} />
      {c && !form && (
        <dl className="kv">
          <dt>Status</dt><dd>{c.enabled ? <Pill tone="ok">On</Pill> : <Pill>Off</Pill>}</dd>
          <dt>Destination</dt><dd>{DESTINATION_LABEL[c.destination]}</dd>
          {c.destination === 's3' && (
            <>
              <dt>Endpoint</dt><dd className="mono small" style={{ wordBreak: 'break-all' }}>{c.s3Endpoint ?? '—'}</dd>
              <dt>Bucket</dt><dd className="mono small">{c.s3Bucket ?? '—'}{c.s3Prefix ? `/${c.s3Prefix}` : ''}</dd>
              <dt>Region</dt><dd className="mono small">{c.s3Region}</dd>
              <dt>Credentials</dt>
              <dd>
                <span className="mono small">{c.s3KeyRef ? `${c.s3KeyRef}_ACCESS_KEY_ID, ${c.s3KeyRef}_SECRET_ACCESS_KEY` : '—'}</span>
                {c.s3KeyRef && <> {c.s3KeyPresent ? <Pill tone="ok">Set in environment</Pill> : <Pill tone="bad">Not set in environment</Pill>}</>}
              </dd>
            </>
          )}
          <dt>Schedule</dt><dd>Daily at {c.scheduleTime} ({c.scheduleTz})</dd>
          <dt>Last run</dt>
          <dd>
            {latest ? (
              <>
                {dateTime(latest.startedAt)} · {latest.trigger === 'schedule' ? 'Scheduled' : 'Run now'}
                {' · '}
                {latest.status === 'ready' ? <Pill tone="ok">{latest.rows ?? 0} rows</Pill>
                  : latest.status === 'error' ? <Pill tone="bad">Failed</Pill>
                  : <Pill>Running</Pill>}
                {latest.status === 'error' && latest.error && <div className="small muted" style={{ marginTop: 4 }}>{latest.error}</div>}
              </>
            ) : 'None'}
          </dd>
          {c.updatedAt && <><dt>Updated</dt><dd>{dateTime(c.updatedAt)}{c.updatedBy ? ` · ${c.updatedBy}` : ''}</dd></>}
        </dl>
      )}
      {c && !form && (
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          <button className="small" onClick={edit}>Edit</button>
          <button className="small" onClick={runNow} disabled={busy !== null}>{busy === 'run' ? 'Writing…' : 'Run now'}</button>
          {last && (
            <>
              <button className="small" onClick={() => download(last, 'ndjson')}>Download last file</button>
              <button className="small" onClick={() => download(last, 'csv')}>Download CSV</button>
              <button className="small" onClick={() => download(last, 'manifest')}>Download manifest</button>
            </>
          )}
        </div>
      )}
      {form && (
        <>
          <Field label="Destination">
            <select value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value as FeedConfig['destination'] })}>
              <option value="directory">{DESTINATION_LABEL.directory}</option>
              <option value="s3">{DESTINATION_LABEL.s3}</option>
            </select>
          </Field>
          {form.destination === 's3' && (
            <>
              <Field label="Endpoint" help="https://s3.ap-south-1.amazonaws.com, or the S3-compatible host in your estate.">
                <input type="url" value={form.s3Endpoint} onChange={(e) => setForm({ ...form, s3Endpoint: e.target.value })} />
              </Field>
              <Field label="Bucket" help="Lower-case letters, digits, dots and hyphens.">
                <input type="text" value={form.s3Bucket} onChange={(e) => setForm({ ...form, s3Bucket: e.target.value })} />
              </Field>
              <Field label="Prefix" help="Object key prefix, no leading slash. Optional.">
                <input type="text" value={form.s3Prefix} onChange={(e) => setForm({ ...form, s3Prefix: e.target.value })} />
              </Field>
              <Field label="Region">
                <input type="text" value={form.s3Region} onChange={(e) => setForm({ ...form, s3Region: e.target.value })} />
              </Field>
              <Field label="Credential reference" help="Environment variable prefix. The API reads <ref>_ACCESS_KEY_ID and <ref>_SECRET_ACCESS_KEY. Keys are not entered here.">
                <input type="text" value={form.s3KeyRef} placeholder="CONSENT_FEED_S3" onChange={(e) => setForm({ ...form, s3KeyRef: e.target.value.toUpperCase() })} />
              </Field>
            </>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: '140px minmax(0,1fr)', gap: 10 }}>
            <Field label="Daily at" help="HH:MM, 24-hour.">
              <input type="time" value={form.scheduleTime} onChange={(e) => setForm({ ...form, scheduleTime: e.target.value })} />
            </Field>
            <Field label="Time zone" help="IANA name, for example Asia/Kolkata.">
              <input type="text" value={form.scheduleTz} onChange={(e) => setForm({ ...form, scheduleTz: e.target.value })} />
            </Field>
          </div>
          <label className="small" style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '4px 0 10px' }}>
            <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
            Run on the schedule
          </label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={() => setForm(null)} disabled={busy !== null}>Cancel</button>
            <button className="primary" onClick={save} disabled={busy !== null || !form.scheduleTime}>{busy === 'save' ? 'Saving…' : 'Save feed'}</button>
          </div>
        </>
      )}
    </Card>
  );
}

export default function DeveloperApi() {
  const owner = getUser()?.role === 'owner';
  const doc = useResource<OpenApiDoc>(() => api.get('/v1/openapi.json'));
  const d = doc.data;
  const origin = window.location.origin;
  const schemes = Object.entries(d?.components?.securitySchemes ?? {});
  const groups = d ? groupByTag(d) : [];
  const webhook = d?.webhooks?.consentChanged?.post;

  function downloadFile() {
    if (!d) return;
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'wekalp-consent-openapi.json';
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <>
      <PageHead title="Developer API">
        <a className="btn" href="/v1/openapi.json" target="_blank" rel="noreferrer">Open JSON</a>
        <button className="primary" onClick={downloadFile} disabled={!d}>Download OpenAPI file</button>
      </PageHead>

      <ErrorNote error={doc.error} />
      {doc.loading && !d && <Loading what="Loading API description" />}

      {d && (
        <>
          <p className="page-line">
            <strong>{groups.reduce((n, g) => n + g.rows.length, 0)}</strong> endpoints · OpenAPI 3.1{d.info?.version ? ` · version ${d.info.version}` : ''}
          </p>

          <Card title="Base address">
            <div className="api-base">{origin}</div>
          </Card>

          <Card title="Credentials">
            {schemes.length === 0 ? <Empty>None.</Empty> : (
              <div className="table-scroll">
                <table className="data">
                  <thead><tr><th>Credential</th><th>Header</th><th>Description</th></tr></thead>
                  <tbody>
                    {schemes.map(([key, s]) => (
                      <tr key={key}>
                        <td><strong>{schemeLabel(key)}</strong></td>
                        <td className="mono">{s.name ?? '—'}</td>
                        <td className="text-list">{s.description ?? ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <Link className="btn small" to="/settings/security">Create a service key</Link>
            </div>
          </Card>

          <Card title="Endpoints">
            {groups.length === 0 ? <Empty>None.</Empty> : (
              <div className="table-scroll">
                <table className="data">
                  <thead><tr><th>Method</th><th>Path</th><th>What it does</th><th>Credential</th></tr></thead>
                  <tbody>
                    {groups.map((g) => [
                      <tr key={`tag-${g.tag}`} className="api-group"><th colSpan={4}>{g.tag}</th></tr>,
                      ...g.rows.map((r) => (
                        <tr key={`${r.method} ${r.path}`}>
                          <td className="api-method">{r.method}</td>
                          <td className="api-path">{r.path}</td>
                          <td>{r.summary}</td>
                          <td className="small">{r.credential}</td>
                        </tr>
                      )),
                    ])}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          {webhook?.description && (
            <Card title="Webhook signature">
              <p className="small" style={{ margin: 0 }}>{webhook.description}</p>
            </Card>
          )}
        </>
      )}

      <DeveloperTools />

      {owner && <ConsentFeedCard />}
    </>
  );
}
